import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { Store } from '@ngxs/store';
import { of, switchMap, forkJoin } from 'rxjs';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { MultiSelectModule } from 'primeng/multiselect';

import { AuthService } from '../../../../services/account/auth.service';
import { AccountCentreService } from '../../../../account-centre/account-centre.service';
import { UtilityService } from '../../../../services/utility/utility.service';
import { UserResponse } from '../../../../models/account/userResponse';
import { UserSelectors } from '../../../../states/user/user-selector';
import { countryResponse } from '../../../../models/country/countryResponse';
import { CourseLevelEnum, CourseLevelEnumDescriptions } from '../../../../models/enums/courseLevelEnum';

@Component({
  selector: 'app-personal-info-card',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, DropdownModule, CalendarModule, MultiSelectModule],
  templateUrl: './personal-info-card.component.html',
  styleUrl: './personal-info-card.component.scss'
})
export class PersonalInfoCardComponent implements OnInit {

  isEditMode = false;
  loading = false;
  userEmail = '';

  basicInfoForm!: FormGroup;
  countries: countryResponse[] = [];
  genders = [
    { name: 'Male', value: 1 },
    { name: 'Female', value: 2 },
    { name: 'Other', value: 3 }
  ];
  collageStartOnTimings: { name: string; key: string }[] = [];
  degreePrefs = [
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.BachelorsDegree], key: CourseLevelEnum.BachelorsDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.MastersDegree], key: CourseLevelEnum.MastersDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.DoctoralDegree], key: CourseLevelEnum.DoctoralDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Diploma], key: CourseLevelEnum.Diploma },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Certificate], key: CourseLevelEnum.Certificate }
  ];
 fieldOfInterestOptions = [
    { name: 'Engineering & Technology', key: 'Engineering & Technology' },
    { name: 'Computer Science & IT', key: 'Computer Science & IT' },
    { name: 'Medicine & Health Sciences', key: 'Medicine & Health Sciences' },
    { name: 'Business & Management', key: 'Business & Management' },
    { name: 'Law & Legal Studies', key: 'Law & Legal Studies' },
    { name: 'Natural Sciences', key: 'Natural Sciences' },
    { name: 'Mathematics & Statistics', key: 'Mathematics & Statistics' },
    { name: 'Architecture & Urban Planning', key: 'Architecture & Urban Planning' },
    { name: 'Arts, Design & Creative Studies', key: 'Arts, Design & Creative Studies' },
    { name: 'Education', key: 'Education' },
    { name: 'Social Sciences & Humanities', key: 'Social Sciences & Humanities' },
    { name: 'Psychology & Counselling', key: 'Psychology & Counselling' },
    { name: 'Agriculture & Veterinary Sciences', key: 'Agriculture & Veterinary Sciences' },
    { name: 'Environmental & Earth Sciences', key: 'Environmental & Earth Sciences' },
    { name: 'Languages & Linguistics', key: 'Languages & Linguistics' },
    { name: 'Communication & Media Studies', key: 'Communication & Media Studies' },
    { name: 'Hospitality, Tourism & Sports', key: 'Hospitality, Tourism & Sports' },
    { name: 'Religion, Philosophy & Ethics', key: 'Religion, Philosophy & Ethics' },
    { name: 'Social Work & Community Services', key: 'Social Work & Community Services' },
    { name: 'Trades, Vocational & Applied Sciences', key: 'Trades, Vocational & Applied Sciences' }
];
  journeyStatusOptions = [
    { name: 'Just Exploring', key: 'Just Exploring' },
    { name: 'Preparing to Apply', key: 'Preparing to Apply' },
    { name: 'Currently Applying', key: 'Currently Applying' },
    { name: 'Already Applied', key: 'Already Applied' }
  ];

  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private authService = inject(AuthService);
  private accountCentreService = inject(AccountCentreService);
  private utilityService = inject(UtilityService);
  private store = inject(Store);

  userDetails: UserResponse | null = null;

  ngOnInit(): void {
    this.buildForm();
    this.setCollageStartOnTimings();

    this.utilityService.getCountries().subscribe({
      next: (res) => this.countries = res.result,
      error: () => this.countries = []
    });

    this.loadUserData();
  }

  buildForm() {
    this.basicInfoForm = this.fb.group({
      firstName:        [{ value: '', disabled: true }],
      lastName:         [{ value: '', disabled: true }],
      gender:           [{ value: '', disabled: true }],
      bornOn:           [{ value: '', disabled: true }],
      homeCountry:      [{ value: '', disabled: true }],
      destination:      [{ value: [], disabled: true }],   // string[] — multi-select
      degreePreference: [{ value: '', disabled: true }],
      fieldOfInterest:  [{ value: '', disabled: true }],
      journeyStatus:    [{ value: '', disabled: true }],
      collageStartOn:   [{ value: '', disabled: true }]
    });
  }

  toggleEditMode() {
    this.isEditMode = !this.isEditMode;
    if (this.isEditMode) {
      this.basicInfoForm.enable();
    } else {
      this.basicInfoForm.disable();
      this.populateForm(); // revert changes
    }
  }

  setCollageStartOnTimings() {
    const currentYear = new Date().getFullYear();
    const terms = ['Spring', 'Fall'];
    for (let yearOffset = 0; yearOffset <= 3; yearOffset++) {
      for (const term of terms) {
        const year = currentYear + yearOffset;
        const key = `${term} ${year}`;
        this.collageStartOnTimings.push({ name: key, key });
      }
    }
  }

  /** Selected destination names shown as chips */
  get selectedDestinations(): string[] {
    const val = this.basicInfoForm.get('destination')?.value;
    if (!val) return [];
    return Array.isArray(val) ? val : [val];
  }

  /** Remove a destination chip */
  removeDestination(name: string) {
    const current: string[] = this.basicInfoForm.get('destination')?.value || [];
    this.basicInfoForm.get('destination')?.setValue(current.filter(v => v !== name));
  }

  private loadUserData() {
    this.store.select(UserSelectors.getUser).pipe(
      switchMap((user) => {
        if (!user || !user.id) return of(null);
        this.userEmail = user.email || '';
        return this.authService.getUserByid(user.id);
      })
    ).subscribe({
      next: (response) => {
        if (response != null) {
          this.userDetails = response;
          this.populateForm();
        }
      }
    });
  }

  populateForm() {
    const rawHomeCountry = this.userDetails?.homeCountry;
    let homeCountryValue: any = null;
    if (rawHomeCountry) {
      homeCountryValue = typeof rawHomeCountry === 'string'
        ? rawHomeCountry.split(',')[0].trim()
        : (Array.isArray(rawHomeCountry) ? rawHomeCountry[0] : rawHomeCountry);
    }

    // Keep destination as a string[] for multi-select
    let destinationValue: string[] = [];
    const rawDest = this.userDetails?.destination;
    if (Array.isArray(rawDest)) {
      destinationValue = rawDest;
    } else if (typeof rawDest === 'string' && rawDest) {
      destinationValue = [rawDest];
    }

    this.basicInfoForm.patchValue({
      firstName:        this.userDetails?.firstName,
      lastName:         this.userDetails?.lastName,
      gender:           this.userDetails?.gender,
      bornOn:           this.userDetails?.bornOn ? new Date(this.userDetails.bornOn) : null,
      homeCountry:      homeCountryValue,
      destination:      destinationValue,
      degreePreference: this.userDetails?.degreePreference,
      fieldOfInterest:  this.userDetails?.intendedMajor,
      journeyStatus:    this.userDetails?.journeyStatus,
      collageStartOn:   this.userDetails?.collageStartOn
    });
  }

  onSubmit() {
    if (this.basicInfoForm.invalid) return;

    if (!this.basicInfoForm.dirty) {
      this.messageService.add({ severity: 'info', summary: 'Info', detail: 'No changes detected.' });
      this.isEditMode = false;
      this.basicInfoForm.disable();
      return;
    }

    this.loading = true;

    const formData = this.basicInfoForm.getRawValue();
    const born: Date | null = formData.bornOn;
    const dateOfBirth = born instanceof Date
      ? `${born.getFullYear()}-${String(born.getMonth() + 1).padStart(2, '0')}-${String(born.getDate()).padStart(2, '0')}`
      : null;

    let destinationPayload: string[] = formData.destination;
    if (!Array.isArray(destinationPayload)) {
      destinationPayload = destinationPayload ? [destinationPayload] : [];
    }

    forkJoin({
      person: this.accountCentreService.updateMyPerson({
        firstName: formData.firstName,
        lastName: formData.lastName,
        gender: formData.gender,
        dateOfBirth
      }),
      student: this.accountCentreService.updateMyStudentProfile({
        destination: destinationPayload,
        degreePreference: formData.degreePreference,
        collageStartOn: formData.collageStartOn,
        homeCountry: formData.homeCountry,
        intendedMajor: formData.fieldOfInterest,
        journeyStatus: formData.journeyStatus
      })
    }).subscribe({
      next: () => {
        this.loading = false;
        this.isEditMode = false;
        this.basicInfoForm.markAsPristine();
        this.basicInfoForm.disable();
        this.loadUserData();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Personal info updated' });
      },
      error: (err) => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Failed to update info.' });
      }
    });
  }
}
